import { useCallback, useEffect, useState } from 'react'
import ConfirmDialog from '../components/ConfirmDialog'
import PlusIcon from '../components/PlusIcon'
import { FormSection } from '../components/SpaceForm'
import { api, fieldErrorsFromDetail } from '../lib/api'
import Markdown from '../lib/markdown'

// The template editor — where a new kind of conversation gets added, without a
// code change. One row carries the agent, the invite email, the two
// participant-facing screens, the evaluator prompt, and the variables a link must
// supply. Laid out like the spaces list: the ice hero with a breadcrumb back to
// Spaces and "+ New template", one table, and the editor as the same pop-up
// the New space dialog is.
//
// Edits take effect immediately for everything read live from the template (call
// screen, thank-you screen, evaluator prompt, agent). The invite email is
// snapshotted onto each link at creation, so editing it here changes future links
// only — the copy under the field says so, because it is the one non-obvious rule
// in here.

const EMPTY = {
  name: '',
  agent_id: '',
  agent_name: '',
  variables: [],
  email_subject: '',
  email_body: '',
  call_screen: '',
  start_label: '',
  thanks_screen: '',
  evaluator_prompt: '',
}

export default function TemplatesPage({ onBack }) {
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [flash, setFlash] = useState(null)
  const [editing, setEditing] = useState(null) // { id | null, draft }
  const [confirmArchive, setConfirmArchive] = useState(null)
  const [busyId, setBusyId] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    api('/api/v1/templates?include_archived=true')
      .then(setRows)
      .catch((err) => setError(err.detail || 'Could not load templates.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(load, [load])

  async function openEditor(id) {
    setFlash(null)
    if (id === null) {
      setEditing({ id: null, draft: { ...EMPTY } })
      return
    }
    setBusyId(id)
    try {
      const template = await api(`/api/v1/templates/${id}`)
      setEditing({
        id,
        draft: {
          name: template.name,
          agent_id: template.agent_id,
          agent_name: template.agent_name ?? '',
          variables: template.variables,
          email_subject: template.email_subject,
          email_body: template.email_body,
          call_screen: template.call_screen,
          start_label: template.start_label,
          thanks_screen: template.thanks_screen,
          evaluator_prompt: template.evaluator_prompt,
        },
      })
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || 'Could not open that template.' })
    } finally {
      setBusyId(null)
    }
  }

  async function handleArchive(row, archived) {
    setBusyId(row.id)
    setFlash(null)
    try {
      await api(`/api/v1/templates/${row.id}/archived`, { method: 'PUT', body: { archived } })
      setFlash({
        type: 'success',
        text: archived
          ? `“${row.name}” is hidden from the composer. Links already on it keep working.`
          : `“${row.name}” is available again.`,
      })
      load()
    } catch (err) {
      setFlash({ type: 'error', text: err.detail || 'Could not change that template.' })
    } finally {
      setConfirmArchive(null)
      setBusyId(null)
    }
  }

  const newButton = (
    <button type="button" className="btn btn--primary" onClick={() => openEditor(null)}>
      <PlusIcon size={16} />
      New template
    </button>
  )

  return (
    <main>
      <section className="disc-band disc-band--alt">
        <div className="hero">
          <div className="hero__text">
            <p className="overline space-crumb hero__eyebrow">
              <button type="button" className="space-crumb__link" onClick={onBack}>
                Spaces
              </button>
              <span aria-hidden="true">›</span>
              <span>Templates</span>
            </p>
            <h1 className="text-h1">Templates</h1>
            <p className="text-body-lg hero__lede">
              One template per kind of conversation: which agent runs it, what the invitation
              says, what the staff member sees, and how the call gets written up.
            </p>
          </div>
          {newButton}
        </div>
      </section>

      <div className="admin-main">
        {flash && (
          <div
            className={`alert alert--${flash.type} text-body-sm`}
            role={flash.type === 'error' ? 'alert' : 'status'}
          >
            {flash.text}
          </div>
        )}

        {loading && (
          <div className="card" role="status">
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              Loading templates…
            </p>
          </div>
        )}

        {!loading && error && (
          <div className="alert alert--error" role="alert">
            <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
              <p className="text-body-md">{error}</p>
              <div>
                <button type="button" className="btn btn--secondary btn--sm" onClick={load}>
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && rows && rows.length === 0 && (
          <div className="spaces-table">
            <div className="spaces-empty">
              <h3 className="text-h5">No templates yet</h3>
              <p className="text-body-sm" style={{ color: 'var(--ink-500)' }}>
                Add one for each kind of conversation you run.
              </p>
              <div style={{ marginTop: 'var(--space-xs)' }}>{newButton}</div>
            </div>
          </div>
        )}

        {!loading && !error && rows && rows.length > 0 && (
          <div className="spaces-table templates-table" role="table" aria-label="Templates">
            <div className="spaces-row spaces-row--head" role="row">
              <span role="columnheader">Template</span>
              <span role="columnheader">Asks for</span>
              <span role="columnheader">Links</span>
              <span role="columnheader" className="sr-only">
                Actions
              </span>
            </div>
            {rows.map((row) => (
              <div
                key={row.id}
                className="spaces-row spaces-row--body"
                role="row"
                onClick={() => openEditor(row.id)}
              >
                <div className="spaces-cell" role="cell">
                  <button
                    type="button"
                    className="spaces-cell__link"
                    onClick={(event) => {
                      event.stopPropagation()
                      openEditor(row.id)
                    }}
                  >
                    <span className="spaces-client">{row.name}</span>
                    <span className="spaces-engagement">
                      {row.archived_at ? 'Hidden from the composer' : 'Available'}
                    </span>
                  </button>
                </div>
                <div className="spaces-cell" role="cell">
                  <span className={row.variables.length === 0 ? 'spaces-zero' : ''}>
                    {row.variables.length === 0
                      ? '—'
                      : row.variables.map((variable) => variable.label).join(', ')}
                  </span>
                </div>
                <div className="spaces-cell" role="cell">
                  <span className={`spaces-cell--num${row.link_count ? '' : ' spaces-zero'}`}>
                    {row.link_count}
                  </span>
                </div>
                <div className="spaces-cell" role="cell" onClick={(event) => event.stopPropagation()}>
                  <span className="roster-actions">
                    <button
                      type="button"
                      className="text-btn"
                      onClick={() =>
                        row.archived_at ? handleArchive(row, false) : setConfirmArchive(row)
                      }
                      disabled={busyId === row.id}
                    >
                      {row.archived_at ? 'Unhide' : 'Hide'}
                    </button>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => openEditor(row.id)}
                      disabled={busyId === row.id}
                    >
                      Edit
                    </button>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <TemplateEditor
          templateId={editing.id}
          initial={editing.draft}
          onCancel={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null)
            setFlash({ type: 'success', text: `“${name}” saved.` })
            load()
          }}
        />
      )}

      {confirmArchive && (
        <ConfirmDialog label="Hide this template?" onClose={() => setConfirmArchive(null)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Hide “{confirmArchive.name}”?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            It disappears from the composer&rsquo;s picker. The {confirmArchive.link_count} link
            {confirmArchive.link_count === 1 ? '' : 's'} already on it keep working, keep running
            calls, and keep rendering their write-ups — this is not a delete.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setConfirmArchive(null)}
              disabled={busyId === confirmArchive.id}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--dark"
              onClick={() => handleArchive(confirmArchive, true)}
              disabled={busyId === confirmArchive.id}
            >
              {busyId === confirmArchive.id ? 'Hiding…' : 'Hide template'}
            </button>
          </div>
        </ConfirmDialog>
      )}
    </main>
  )
}

const VARIABLE_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/

// The fields that have an input to attach a message to. A server error naming
// anything else (`variables`, or a field added server-side later) goes to the
// banner instead of disappearing.
const FIELDS_WITH_INPUTS = new Set([
  'name',
  'agent_id',
  'agent_name',
  'email_subject',
  'email_body',
  'call_screen',
  'start_label',
  'thanks_screen',
  'evaluator_prompt',
])

// The editor, as the same pop-up the New space dialog is: an ice head, five
// numbered sections, a foot with Cancel and the primary.
function TemplateEditor({ templateId, initial, onCancel, onSaved }) {
  const [draft, setDraft] = useState(initial)
  const [fieldErrors, setFieldErrors] = useState({})
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(false)

  function set(field, value) {
    setDraft((current) => ({ ...current, [field]: value }))
  }

  function setVariable(index, patch) {
    setDraft((current) => ({
      ...current,
      variables: current.variables.map((variable, i) =>
        i === index ? { ...variable, ...patch } : variable,
      ),
    }))
  }

  function addVariable() {
    setDraft((current) => ({
      ...current,
      variables: [...current.variables, { key: '', label: '', required: true, help: '' }],
    }))
  }

  function removeVariable(index) {
    setDraft((current) => ({
      ...current,
      variables: current.variables.filter((_, i) => i !== index),
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (saving) return

    const errors = {}
    if (!draft.name.trim()) errors.name = 'Name is required.'
    if (!/agent_[A-Za-z0-9]+/.test(draft.agent_id))
      errors.agent_id = 'Paste the ElevenLabs agent id (agent_…) or its dashboard URL.'
    if (!draft.email_subject.trim()) errors.email_subject = 'Required.'
    if (!draft.email_body.trim()) errors.email_body = 'Required.'
    else if (!draft.email_body.includes('{LINK}'))
      errors.email_body = 'The body must include {LINK} — otherwise the email has no link in it.'
    if (!draft.call_screen.trim()) errors.call_screen = 'Required.'
    if (!draft.thanks_screen.trim()) errors.thanks_screen = 'Required.'
    if (!draft.evaluator_prompt.trim()) errors.evaluator_prompt = 'Required.'
    draft.variables.forEach((variable, index) => {
      if (!VARIABLE_KEY_RE.test(variable.key)) {
        errors[`variable-${index}`] =
          'Keys are lowercase letters, digits and underscores, starting with a letter.'
      } else if (!variable.label.trim()) {
        errors[`variable-${index}`] = 'Give the field a label.'
      }
    })
    setFieldErrors(errors)
    setError(null)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const body = {
        ...draft,
        start_label: draft.start_label?.trim() || null,
        agent_name: draft.agent_name?.trim() || null,
        variables: draft.variables.map((variable) => ({
          key: variable.key.trim(),
          label: variable.label.trim(),
          required: Boolean(variable.required),
          help: variable.help?.trim() || null,
        })),
      }
      const saved = templateId
        ? await api(`/api/v1/templates/${templateId}`, { method: 'PUT', body })
        : await api('/api/v1/templates', { method: 'POST', body })
      onSaved(saved.name)
    } catch (err) {
      const mapped = fieldErrorsFromDetail(err.detail)
      if (mapped) {
        setFieldErrors(mapped)
        // A 422 can name something this form has no input for — `variables` as a
        // whole, say. Those messages would vanish silently, so anything without a
        // field of its own goes to the banner.
        const orphans = Object.entries(mapped)
          .filter(([field]) => !FIELDS_WITH_INPUTS.has(field))
          .map(([, message]) => message)
        setError(orphans.length > 0 ? orphans.join(' ') : null)
      } else {
        setError(err.detail || 'Could not save this template.')
      }
      setSaving(false)
    }
  }

  const title = templateId ? 'Edit template' : 'New template'

  return (
    <ConfirmDialog label={title} onClose={onCancel} wide className="modal--form">
      <form onSubmit={handleSubmit} noValidate>
        <div className="dialog-head">
          <p className="overline hero__eyebrow">Templates</p>
          <h3 className="new-space__title">{title}</h3>
          <p className="dialog-head__lede">
            {templateId
              ? 'The screens, the agent and the evaluator prompt change for open links straight away; the invitation changes future links only.'
              : 'One kind of conversation: its agent, its invitation, what the staff member sees, and how a call is written up.'}
          </p>
        </div>

        <div className="dialog-body">
          {error && (
            <div className="alert alert--error text-body-sm" role="alert">
              {error}
            </div>
          )}

          <div className="space-form">
            <FormSection number={1} title="Template" compact>
              <div className="form-fields">
                <div className="space-form__grid">
                  <Field
                    label="Name"
                    id="tpl-name"
                    value={draft.name}
                    error={fieldErrors.name}
                    onChange={(value) => set('name', value)}
                    hint="What this kind of conversation is called, e.g. “Knowledge capture”."
                  />
                  <Field
                    label="ElevenLabs agent"
                    id="tpl-agent"
                    value={draft.agent_id}
                    error={fieldErrors.agent_id}
                    onChange={(value) => set('agent_id', value)}
                    hint="The agent id, or paste its dashboard URL."
                  />
                  <Field
                    label="Agent's name"
                    id="tpl-agent-name"
                    value={draft.agent_name}
                    error={fieldErrors.agent_name}
                    onChange={(value) => set('agent_name', value)}
                    hint="What the agent calls itself — shown beside its words during the call. Leave blank for no name."
                  />
                </div>
              </div>
            </FormSection>

            <FormSection number={2} title="What each link must supply" compact>
              <div className="form-fields">
                <p className="field__help">
                  One field per entry. Each becomes{' '}
                  <code className="md-code">{'{key}'}</code> in the copy below (either case), and
                  reaches the agent as a dynamic variable under the same key — so the agent&rsquo;s
                  prompt must use <code className="md-code">{'{{key}}'}</code> to receive it.
                </p>

                {draft.variables.length === 0 && (
                  <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                    None — every link on this template is identical apart from who gets it.
                  </p>
                )}

                {/* One row per variable: key · label · hint · required · remove.
                    Column headers carry the labels so the row itself is four
                    bare inputs; each input keeps an aria-label. Stacks below
                    md, where four-across is unreadable. */}
                {draft.variables.length > 0 && (
                  <div className="var-row var-row--head" aria-hidden="true">
                    <span>Key</span>
                    <span>Label</span>
                    <span>Hint (optional)</span>
                    <span>Required</span>
                    <span />
                  </div>
                )}

                <div className="flex flex-col" style={{ gap: 'var(--space-xs)' }}>
                  {draft.variables.map((variable, index) => (
                    <div key={index}>
                      <div className="var-row">
                        <input
                          type="text"
                          className={`field__input${fieldErrors[`variable-${index}`] ? ' field__input--invalid' : ''}`}
                          value={variable.key}
                          aria-label={`Variable ${index + 1} key`}
                          placeholder="topic"
                          onChange={(e) => setVariable(index, { key: e.target.value })}
                        />
                        <input
                          type="text"
                          className="field__input"
                          value={variable.label}
                          aria-label={`Variable ${index + 1} label`}
                          placeholder="Topic"
                          onChange={(e) => setVariable(index, { label: e.target.value })}
                        />
                        <input
                          type="text"
                          className="field__input"
                          value={variable.help || ''}
                          aria-label={`Variable ${index + 1} hint`}
                          placeholder="What to type here"
                          onChange={(e) => setVariable(index, { help: e.target.value })}
                        />
                        <label className="var-row__check">
                          <input
                            type="checkbox"
                            checked={Boolean(variable.required)}
                            onChange={(e) => setVariable(index, { required: e.target.checked })}
                          />
                          <span className="md:hidden text-body-sm">Required</span>
                        </label>
                        <button
                          type="button"
                          className="var-row__remove"
                          onClick={() => removeVariable(index)}
                          aria-label={`Remove ${variable.label || variable.key || `variable ${index + 1}`}`}
                          title="Remove this field"
                        >
                          ×
                        </button>
                      </div>
                      {fieldErrors[`variable-${index}`] && (
                        <p className="field__error">{fieldErrors[`variable-${index}`]}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={addVariable}>
                    Add a field
                  </button>
                </div>
              </div>
            </FormSection>

            <FormSection number={3} title="Invitation email" compact>
              <div className="form-fields">
                <p className="field__help">
                  Each new space starts from this wording, and each link snapshots its space&rsquo;s,
                  so changes here affect <strong>future spaces only</strong>. {'{LINK}'} is
                  required; {'{EXPIRY_DATE}'} and {'{FIRST_NAME}'} resolve at send.
                </p>
                <Field
                  label="Subject"
                  id="tpl-subject"
                  value={draft.email_subject}
                  error={fieldErrors.email_subject}
                  onChange={(value) => set('email_subject', value)}
                />
                <TextArea
                  label="Body"
                  id="tpl-body"
                  rows={10}
                  value={draft.email_body}
                  error={fieldErrors.email_body}
                  onChange={(value) => set('email_body', value)}
                />
              </div>
            </FormSection>

            <FormSection number={4} title="What the staff member sees" compact>
              <div className="form-fields">
                <p className="field__help">
                  Markdown. These are read live, so edits change what open links show immediately.
                  If this conversation needs a notice or a consent line before the call, it belongs
                  in the call screen.
                </p>
                <TextArea
                  label="Call screen (before the button)"
                  id="tpl-call-screen"
                  rows={8}
                  value={draft.call_screen}
                  error={fieldErrors.call_screen}
                  onChange={(value) => set('call_screen', value)}
                />
                <Field
                  label="Start button"
                  id="tpl-start-label"
                  value={draft.start_label || ''}
                  onChange={(value) => set('start_label', value)}
                  hint="Leave blank for “Agree and start”."
                />
                <TextArea
                  label="Thank-you screen (after any call ends)"
                  id="tpl-thanks"
                  rows={6}
                  value={draft.thanks_screen}
                  error={fieldErrors.thanks_screen}
                  onChange={(value) => set('thanks_screen', value)}
                  hint="{CALL_ENDED_AT} becomes the time their call ended."
                />
                <div>
                  <button
                    type="button"
                    className="text-btn"
                    onClick={() => setShowPreview((current) => !current)}
                  >
                    {showPreview ? 'Hide preview' : 'Preview the screens'}
                  </button>
                </div>
                {showPreview && (
                  <div className="flex flex-col" style={{ gap: 'var(--space-s)' }}>
                    {/* Placeholders stay literal here on purpose: this previews
                        the markdown, and there is no participant to resolve
                        them against. */}
                    <div className="card card--alt">
                      <p className="overline">Call screen</p>
                      <Markdown source={draft.call_screen} className="markdown--intro" />
                    </div>
                    <div className="card card--alt">
                      <p className="overline">Thank-you screen</p>
                      <Markdown source={draft.thanks_screen} className="markdown--intro" />
                    </div>
                  </div>
                )}
              </div>
            </FormSection>

            <FormSection number={5} title="Evaluator prompt" compact>
              <div className="form-fields">
                <p className="field__help">
                  Claude&rsquo;s system prompt for writing up a call on this template. It gets the
                  transcript and returns <strong>markdown</strong>, and this prompt alone decides the
                  write-up&rsquo;s shape — ask for the headings and sections you want. Editing it
                  changes future write-ups; to re-run an existing call against it, use{' '}
                  <code className="md-code">make redo-eval</code>.
                </p>
                <TextArea
                  label="Prompt"
                  id="tpl-eval"
                  rows={14}
                  value={draft.evaluator_prompt}
                  error={fieldErrors.evaluator_prompt}
                  onChange={(value) => set('evaluator_prompt', value)}
                />
              </div>
            </FormSection>
          </div>
        </div>

        <div className="dialog-foot dialog-foot--flush">
          <p className="dialog-foot__note">
            {templateId
              ? 'Links already sent keep the invitation they were drafted with.'
              : 'Nothing runs on a template until a space is created on it.'}
          </p>
          <div className="dialog-foot__actions">
            <button
              type="button"
              className="btn btn--secondary btn--outlined"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : templateId ? 'Save template' : 'Create template'}
            </button>
          </div>
        </div>
      </form>
    </ConfirmDialog>
  )
}

function Field({ label, id, value, onChange, error, hint }) {
  return (
    <div className="field w-full">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        className={`field__input${error ? ' field__input--invalid' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <p className="field__error">{error}</p> : hint && <p className="field__help">{hint}</p>}
    </div>
  )
}

function TextArea({ label, id, value, onChange, error, hint, rows = 8 }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        className={`field__input${error ? ' field__input--invalid' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? <p className="field__error">{error}</p> : hint && <p className="field__help">{hint}</p>}
    </div>
  )
}
